import { Dispatch, SetStateAction, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { disableDecomModels, UserKeys } from '../../../../api/users-api';
import { handleErrors } from '../../../../utils/errors';
import SharedDialog from '../../../../shared/shared-dialog';
import ChangeLogDialog from '../../../../shared/change-log-dialog';
import { useStore } from '../../../state/zustand-store';
import { useAnalytics } from '../../../../shared/analytics/create-events';
import * as analytics from '../../../../shared/analytics/analytics.json';
import { ModuleList } from '../../../../shared/analytics/module-list';
import {
  isAbleToSeeUIElement,
  ToolsRoles,
} from '../../../../utils/roles/client-roles';
import { DecomModel } from '../../../../../types';
import Typography from '@mui/material/Typography';

interface DisableDecomPoliciesDialogProps {
  open: boolean;
  handleClose: () => void;
  selectedRows: string[];
  setSelectedRows: Dispatch<SetStateAction<string[]>>;
  changeLogRoles: string[];
  models: DecomModel[];
}

export default function DisableDecomPoliciesDialog({
  open,
  handleClose,
  selectedRows,
  setSelectedRows,
  changeLogRoles,
  models,
}: DisableDecomPoliciesDialogProps) {
  const { createEvent } = useAnalytics();
  const { setSnackbar } = useStore();
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');
  const queryClient = useQueryClient();

  const selectedModels =
    models.filter((item) => selectedRows.includes(item.id)) || [];

  const { mutate, isPending } = useMutation({
    mutationFn: () => disableDecomModels(selectedRows, changeLogMessage),
    onSuccess: () => {
      setSnackbar({
        open: true,
        severity: 'success',
        message:
          selectedRows.length === 1
            ? 'The selected decom policy has been disabled.'
            : 'The selected decom policies have been disabled.',
      });

      queryClient.setQueryData(
        [UserKeys.DecomModels],
        (oldData: DecomModel[]) => {
          const newData = oldData.map((model) => {
            if (selectedRows.includes(model.id)) {
              return {
                ...model,
                isActive: false,
              };
            } else {
              return model;
            }
          });
          return newData;
        }
      );

      setSelectedRows([]);
      setChangeLogMessage('');
      handleClose();
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage:
          selectedRows.length === 1
            ? 'There was an issue disabling the selected policy. Please refresh and try again.'
            : 'There was an issue disabling the selected policies. Please refresh and try again.',
      });
    },
    onSettled: () => {
      createEvent(analytics.automation.decommissioning.disable_decom_policies, {
        module: ModuleList.modules.automation.name,
        section: ModuleList.modules.automation.sections.decommissioning,
      });
      setOpenChangeLogDialog(false);
    },
  });

  function handleSubmit() {
    if (
      isAbleToSeeUIElement(
        changeLogRoles,
        ToolsRoles.DisableDecommissioningProcesses.value
      )
    ) {
      setOpenChangeLogDialog(true);
    } else {
      mutate();
    }
  }

  return (
    <SharedDialog
      open={open}
      handleClose={handleClose}
      title={`Disable Decommissioning ${
        selectedRows.length === 1 ? 'Policy' : 'Policies'
      }`}
      dialogContent={
        <>
          {selectedRows.length > 1 && (
            <Typography>
              Disable{' '}
              <strong>{selectedRows.length} decommissioning policies.</strong>
            </Typography>
          )}
          {selectedRows.length === 1 && (
            <Typography>
              Disable decommissioning policy{' '}
              <strong>{selectedModels[0].name}</strong>.
            </Typography>
          )}
          <ChangeLogDialog
            open={openChangeLogDialog}
            handleClose={() => setOpenChangeLogDialog(false)}
            changeLogMessage={changeLogMessage}
            setChangeLogMessage={setChangeLogMessage}
            mutate={mutate}
            mutateLoading={isPending}
          />
        </>
      }
      primaryButton={{
        title: `Disable ${selectedRows.length === 1 ? 'Policy' : 'Policies'}`,
        action: handleSubmit,
        loading: isPending,
        disabled: isPending,
        color: 'warning',
      }}
    />
  );
}
